// The platform's better-auth client. Not authored surface: the box restores this file from the
// template on every sync, and `isAgentAppWritable` refuses a write under `src/lib/platform/`.
//
// It is here because the alternative was MEASURED and lost. The auth surface this app serves
// carries a handful of facts no signature and no paragraph conveys, and a fleet reading of ten
// generated apps found one prose-conveyed fact — that every better-auth route is mounted under
// `/api/auth/` — guessed right by nine authors and wrong by one, which shipped machine 7mqy1gk3p
// unusable: sign-in 404'd against the SPA fallback and nobody could enter the product. The facts
// below are four more of the same kind, each one a silent wrong answer rather than a loud one:
//
//  1. `GET /api/auth/get-session` answers a signed-OUT browser with **200 and a body of literal
//     `null`**, not 401. `res.ok` is true, so the discriminator is the BODY. And a backend that
//     never answers is a third state — see `SessionStatus` below.
//  2. `user.role` is a COMMA-JOINED STRING server-side (`appRoles.join(',')` in the first-signup
//     operator grant), split at three places on the server and at none on the wire. A declared
//     `string[]` return type says nothing about that; `parseRoles` is where the split lives.
//  3. This app's own refusals ("That email address is reserved.", every password-policy message)
//     are `{ message }` with NO `code`, while better-auth's own are `{ message, code }`. An
//     `errorMessage` that switches on `code` drops every platform refusal on the floor, and
//     better-call prefixes a validation message with the failing path (`[body.email] …`).
//  4. `POST /api/auth/sign-in/social` answers **200 `{ url }`** with a `Location` header no
//     `fetch` follows. The app runs inside the preview IFRAME, so a bare `location.assign` moves
//     the preview frame instead of the app — `signInWithProvider` opens a popup when framed.
//
// The sign-in and sign-up PAGES are entirely the author's — this file has no opinion about what
// they look like, which routes are public, or where anyone lands.

const AUTH_NAMESPACE = '/api/auth';

/**
 * Re-home a path into the auth namespace. Deliberately LOSSY — anything not already under
 * `/api/auth/` is moved there, so `postAuth` can no longer reach any other route. Every correct
 * caller passes an `/api/auth/*` path already, which makes this a no-op on them and idempotent on
 * a repeated one; an incorrect caller gets a working sign-in instead of a 404 nobody can enter.
 *
 * Order is load-bearing: repair the missing leading slash FIRST, then test the prefix. The other
 * way round, `'api/auth/sign-in/email'` fails the prefix test and comes out
 * `/api/auth/api/auth/sign-in/email`.
 */
export function authEndpointPath(path: string): string {
  // An empty path is not an auth call at all — `fetch('')` addresses the current document. Naming
  // it `/api/auth/` would invent an endpoint the caller never asked for, so leave it alone.
  if (path.length === 0) return path;
  // Absolute (`https://host/api/auth/…`) or protocol-relative: the caller chose an origin, and a
  // path prefix cannot be spliced into one without changing where it points.
  if (path.startsWith('//') || /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(path)) return path;
  const rooted = path.startsWith('/') ? path : `/${path}`;
  if (rooted.startsWith(`${AUTH_NAMESPACE}/`)) return rooted;
  return `${AUTH_NAMESPACE}${rooted}`;
}

/**
 * POST to an auth route. The escape hatch for a route the named operations below do not wrap —
 * reach for `signInWithEmail` / `signUpWithEmail` / `signInWithProvider` / `signOut` first.
 */
export async function postAuth(path: string, body?: Record<string, string>): Promise<Response> {
  // The content-type promises JSON, so the body must BE JSON. `fetch(url, { body: '' })` still
  // attaches a request body, and better-call then calls `request.json()` on it, throws a
  // SyntaxError and answers 400 "Invalid JSON in request body" — on sign-out that means the
  // session SURVIVES a sign-out the UI already reported as done.
  return fetch(authEndpointPath(path), {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
}

export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * The human sentence behind a failed auth response.
 *
 * Reads `message` and NOTHING ELSE — every refusal this app can produce carries one, while only
 * better-auth's own carry a `code`, so a `code`-keyed lookup silently generifies "That email
 * address is reserved.", "Password must be at least 8 characters.", "This password is too common.
 * Choose something less guessable." and "Password can't be your email address." The leading
 * `[…] ` better-call puts in front of a validation message names the failing request path
 * (`[body.email] Invalid email`) and means nothing to the person typing, so it comes off.
 */
export async function errorMessage(res: Response): Promise<string> {
  try {
    const payload = (await res.json()) as { message?: unknown };
    if (typeof payload.message === 'string' && payload.message.length > 0) {
      const cleaned = payload.message.replace(/^\[[^\]]*\]\s*/, '').trim();
      return cleaned.length > 0 ? cleaned : GENERIC_ERROR_MESSAGE;
    }
  } catch {
    // A non-JSON error body (an edge 502, an HTML fallback) — fall through to the generic message.
  }
  return GENERIC_ERROR_MESSAGE;
}

/**
 * `unavailable` is NOT `anonymous`. A backend that never answers `get-session` is a different fact
 * from a reachable backend reporting no session, and conflating them either blanks the app forever
 * (stuck on `unknown`) or bounces a signed-in person to /sign-in. Render an honest error state for
 * `unavailable`; only `anonymous` means "sign in".
 */
export type SessionStatus = 'unknown' | 'authed' | 'anonymous' | 'unavailable';

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  /** Already split. The server stores these comma-joined on `user.role`. */
  roles: string[];
}

export interface SessionInfo {
  status: SessionStatus;
  user?: SessionUser;
}

/** THE comma split. `'admin,librarian'` is one string on the wire and two roles in the app. */
function parseRoles(role: unknown): string[] {
  if (typeof role !== 'string' || role.length === 0) return [];
  return role.split(',').filter((value) => value.length > 0);
}

function readUser(payload: unknown): SessionUser | undefined {
  if (payload === null || typeof payload !== 'object') return undefined;
  const user = (payload as { user?: unknown }).user;
  if (user === null || typeof user !== 'object') return undefined;
  const fields = user as { id?: unknown; name?: unknown; email?: unknown; role?: unknown };
  return {
    id: typeof fields.id === 'string' ? fields.id : '',
    name: typeof fields.name === 'string' ? fields.name : '',
    email: typeof fields.email === 'string' ? fields.email : '',
    roles: parseRoles(fields.role),
  };
}

const PROBE_TIMEOUT_MS = 10_000;
const PROBE_RETRIES = 2;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function probeSessionOnce(): Promise<SessionInfo> {
  try {
    const res = await fetch(`${AUTH_NAMESPACE}/get-session`, {
      credentials: 'include',
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    // A REACHABLE backend answering non-ok is anonymous (fail-closed). It is only ever a timeout or
    // a transport error that means unreachable — see the catch.
    if (!res.ok) return { status: 'anonymous' };
    const payload: unknown = await res.json().catch(() => null);
    // THE 200-null discriminator: signed out is a successful response carrying `null`.
    if (payload === null) return { status: 'anonymous' };
    return { status: 'authed', user: readUser(payload) };
  } catch {
    return { status: 'unavailable' };
  }
}

/**
 * Ask the server who is signed in. Bounded and retried, so a wedged backend resolves to
 * `unavailable` rather than leaving the app blank forever.
 */
export async function probeSession(): Promise<SessionInfo> {
  for (let attempt = 0; attempt <= PROBE_RETRIES; attempt += 1) {
    const info = await probeSessionOnce();
    if (info.status !== 'unavailable') return info;
    if (attempt < PROBE_RETRIES) await delay(500 * (attempt + 1));
  }
  return { status: 'unavailable' };
}

let session: SessionInfo = { status: 'unknown' };
const listeners = new Set<() => void>();
let started = false;
/** The in-flight boot, so every caller that needs a settled session joins ONE probe. */
let booting: Promise<void> | undefined;

function publish(next: SessionInfo): void {
  session = next;
  for (const listener of listeners) listener();
}

/**
 * The session AS PUBLISHED, without subscribing and without waiting. Correct for a render path,
 * where the store is already settled and a synchronous read is what React wants.
 *
 * NOT for a router guard. `beforeLoad` runs before any component mounts, so nothing has subscribed
 * yet and the boot probe has not been REQUESTED — this returns `'unknown'`, the guard's `authed` /
 * `anonymous` branch does not fire, and `beforeLoad` never runs again when the session lands a beat
 * later. A signed-in person then sits on the public landing page, which is what a live drive of
 * s4464pv79 reported. A guard must `await sessionSettled()` instead.
 */
export function getSession(): SessionInfo {
  return session;
}

/**
 * Re-ask the server and publish the answer. Called for you after every operation below (the boot
 * probe has its own path, `bootSession`); call it yourself only when something OUTSIDE this module
 * changed the cookie.
 */
export async function refreshSession(): Promise<SessionInfo> {
  const next = await probeSession();
  publish(next);
  return next;
}

// The session cookie is `SameSite=None; Secure` and httpOnly, so nothing in this document can read
// it — `get-session` is the only witness. That is why a session established out of band (a social
// popup finishing, a sibling tab signing in) is invisible until we ask again, and why coming BACK
// to the page is the moment to ask. Gated on not-already-authed, the way the shipped app gated its
// per-navigation re-probe: an authed session needs no confirming, and re-probing one on every tab
// switch is a request per focus for nothing.
function armReturnReprobe(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const onReturn = (): void => {
    if (session.status === 'authed') return;
    if (document.visibilityState !== 'visible') return;
    void refreshSession();
  };
  document.addEventListener('visibilitychange', onReturn);
  window.addEventListener('focus', onReturn);
}

// ─── The preview session ─────────────────────────────────────────────────────
//
// Inside the studio this app is an iframe, and a builder looking at the product they are paying for
// must not be looking at it signed OUT. `POST /api/auth/preview-sign-in` mints them a session on
// this origin — but the mint is gated on the BUILDER's Clerk token, which lives in the studio and
// never in this document. The studio hands it over only through a request/response pair on the
// preview bridge, so the sequence is ASK, AWAIT, then post: a POST on boot with no bearer is a 401
// every time.
//
// This lives in the auth client rather than in the shell script that carries the navigation bridge
// because all three of its orderings belong to this module and to nothing else. It must run only
// when the probe already came back `anonymous` — minting over a live session would replace whoever
// is signed in with the preview owner. It must publish the re-probe afterwards, or the app holds a
// stale anonymous session while the cookie on the wire says otherwise, which is the failure mode a
// shell-owned mint would have to reach back through a global to avoid. And it must happen exactly
// ONCE, at boot: that is what makes a sign-out stick, because `refreshSession` — the return-to-page
// re-probe, every post-operation refresh — never mints, so signing out inside the preview does not
// sign the person straight back in on the next tab switch.

const PREVIEW_TOKEN_REQUEST = 'onauto:request-preview-token';
const PREVIEW_TOKEN_RESPONSE = 'onauto:preview-token';
const PREVIEW_TOKEN_TIMEOUT_MS = 4000;
const PREVIEW_IDENTITY_REPORT = 'onauto:preview-identity';

// ─── The requested identity ──────────────────────────────────────────────────
//
// WHO the studio wants this preview to be, carried on the iframe's `src` fragment. It cannot ride
// the token handshake: that handshake only happens when the boot probe already came back anonymous,
// and the session cookie survives an iframe remount — so from the second switch onward this document
// is `authed`, never asks, and never learns what was requested. Asking unconditionally instead would
// make every published app that some other page frames wait on a reply that is not coming.
//
// LATCHED AT MODULE SCOPE, which is the only point where reading it is safe. The hash does not
// survive the first navigation: the shell's bridge pushes a bare pathname, and a router redirect
// builds its target without one. Module evaluation happens before the router exists, before React
// mounts, and before any postMessage can be dispatched.
//
// The value is OPAQUE. The server owns the grammar (`owner`, `anonymous`, `role:<slug>`) exactly as
// it owns the account namespace; this document forwards the token and never parses it. It is also
// raw, never percent-encoded — the studio builds the same spelling for the query and the fragment,
// and an encoding asymmetry here would be refused by the server's allowlist and land the builder on
// the owner's screen with no error anywhere.
const PREVIEW_FRAGMENT_KEY = '#onauto-preview-as=';

/** The token ask, bounded tighter when a fragment asked for it: a fragment is attacker-supplied, so
 *  a page that frames a published app can force this leg, and every route guard awaits it. */
const PREVIEW_FRAGMENT_TOKEN_TIMEOUT_MS = 1000;
const PREVIEW_MINT_TIMEOUT_MS = 5000;

function readBootFragment(): string | null {
  // `globalThis.location` and a try/catch, not `window.location`: this runs during module
  // evaluation, and the platform client is exercised in a plain node environment where neither
  // exists. An unguarded read would reject every consumer of `sessionSettled()` at import time.
  try {
    const hash = globalThis.location?.hash ?? '';
    return hash.startsWith(PREVIEW_FRAGMENT_KEY) ? hash.slice(PREVIEW_FRAGMENT_KEY.length) : null;
  } catch {
    return null;
  }
}

const BOOT_FRAGMENT = readBootFragment();

/**
 * Tell the studio which identity this document actually settled on, so the row it highlights is a
 * report rather than an assertion. Sent ONLY when a fragment asked for one: with nothing requested
 * there is nothing to verify, and silence after a request is itself the signal that this bundle
 * predates the feature.
 *
 * Target origin `*` for the same reason the token request uses it — this document cannot know the
 * studio's origin until the studio speaks, and the payload is a role slug the app serves anyway.
 * The studio verifies OUR origin, which is the direction that matters.
 */
function reportPreviewIdentity(identity: string | null): void {
  if (typeof window === 'undefined' || !isFramed()) return;
  try {
    window.parent.postMessage({ type: PREVIEW_IDENTITY_REPORT, identity }, '*');
  } catch {
    // A studio that refuses the message is not this document's problem.
  }
}

/**
 * Ask the studio for the builder's short-lived token. Resolves `null` on a refusal, on an answer
 * that is not one, and on SILENCE — a published app that some other page has framed must resolve
 * and carry on, never hang waiting for a reply that is not coming.
 */
function requestPreviewToken(timeoutMs: number): Promise<string | null> {
  const studio = window.parent;
  const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout>;
    const settle = (token: string | null): void => {
      clearTimeout(timer);
      window.removeEventListener('message', listener);
      resolve(token);
    };
    const listener = (event: MessageEvent): void => {
      if (event.source !== studio) return;
      const data: unknown = event.data;
      if (data === null || typeof data !== 'object') return;
      const message = data as { type?: unknown; id?: unknown; token?: unknown };
      // The id is what keeps two overlapping asks from answering each other, and the studio
      // deliberately answers an absent token with `null` rather than staying quiet.
      if (message.type !== PREVIEW_TOKEN_RESPONSE || message.id !== id) return;
      settle(typeof message.token === 'string' && message.token.length > 0 ? message.token : null);
    };
    window.addEventListener('message', listener);
    timer = setTimeout(() => settle(null), timeoutMs);
    // Target origin `*`: this document cannot know the studio's origin until the studio speaks, and
    // the request carries nothing — the SECRET travels the other way, and the studio verifies our
    // origin before it parts with it.
    studio.postMessage({ type: PREVIEW_TOKEN_REQUEST, id }, '*');
  });
}

/**
 * Trade the studio's token for a session cookie on this origin. Answers the session that resulted,
 * or `null` when there was nothing to trade: not framed, no token offered, or the mint refused.
 */
/** What a mint attempt came back with: the session it produced, and the identity the SERVER says it
 *  established — `null` when it established nothing (a decline, a refused role, or a backend that
 *  predates the field and cannot name what it did). */
interface MintOutcome {
  session: SessionInfo;
  identity: string | null;
}

async function establishPreviewSession(as: string | null): Promise<MintOutcome | null> {
  if (typeof window === 'undefined' || !isFramed()) return null;
  const token = await requestPreviewToken(as === null ? PREVIEW_TOKEN_TIMEOUT_MS : PREVIEW_FRAGMENT_TOKEN_TIMEOUT_MS);
  if (token === null) return null;
  let mint: Response;
  try {
    mint = await fetch(`${AUTH_NAMESPACE}/preview-sign-in`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        authorization: `Bearer ${token}`,
        ...(as === null ? {} : { 'content-type': 'application/json' }),
      },
      ...(as === null ? {} : { body: JSON.stringify({ as }) }),
      signal: AbortSignal.timeout(PREVIEW_MINT_TIMEOUT_MS),
    });
  } catch {
    return null;
  }
  // A refusal here is the ordinary case for anyone who is not the workspace's builder — 401 from
  // the token gate, 403 from the authorization check. Neither is an error the app should show;
  // it just means this browser stays anonymous.
  if (!mint.ok) return null;
  // The identity comes from the BODY, never from the status. A published app answers this route
  // from its SPA fallback with a cheerful 200 and an HTML document, so `mint.ok` alone can say a
  // session was established when none was. The re-probe below is what settles the session itself.
  const identity = await readMintedIdentity(mint);
  // A single attempt on the requested path: the retrying probe is three attempts and two backoffs,
  // and every route guard in the app is awaiting this.
  return { session: as === null ? await probeSession() : await probeSessionOnce(), identity };
}

async function readMintedIdentity(mint: Response): Promise<string | null> {
  try {
    const body = (await mint.json()) as { identity?: unknown } | null;
    return typeof body?.identity === 'string' ? body.identity : null;
  } catch {
    return null;
  }
}

/**
 * The first probe, and the ONE place a preview session is minted. It publishes once, at the end:
 * publishing `anonymous` first and upgrading a beat later would flash the signed-out app and bounce
 * it through whatever guard the author wrote, which is the exact thing the mint exists to prevent.
 */
async function bootSession(): Promise<void> {
  const first = await probeSession();
  if (BOOT_FRAGMENT === null) {
    // Nothing was requested, so this is the path it has always been: mint the owner only over an
    // anonymous probe, and never ask when somebody is already signed in.
    if (first.status !== 'anonymous') {
      publish(first);
      return;
    }
    publish((await establishPreviewSession(null))?.session ?? first);
    return;
  }
  // An identity WAS requested. Anonymous over an already-anonymous probe is the one case with
  // nothing to do — and it is the Public row on a fresh preview, so short-circuiting it skips a
  // round trip whose failure mode is a stalled route guard.
  if (BOOT_FRAGMENT === 'anonymous' && first.status === 'anonymous') {
    publish(first);
    reportPreviewIdentity('anonymous');
    return;
  }
  const outcome = await establishPreviewSession(BOOT_FRAGMENT);
  publish(outcome?.session ?? first);
  reportPreviewIdentity(outcome?.identity ?? null);
}

/**
 * Watch the session. The FIRST subscriber starts it: one probe now — which is also the moment a
 * framed preview trades the builder's token for a session — and a re-probe whenever the page is
 * returned to while nobody is signed in. Returns the unsubscribe.
 */
/**
 * The session, SETTLED — start the boot if nothing has yet, join it if it is in flight, and return
 * at once once it has landed. This is what a router guard reads: `beforeLoad` may be async, and a
 * guard that must know whether somebody is signed in has to be able to wait for the answer rather
 * than read a placeholder and decide on it.
 */
export async function sessionSettled(): Promise<SessionInfo> {
  if (!started) {
    started = true;
    armReturnReprobe();
    booting = bootSession();
  }
  await booting;
  return session;
}

export function subscribeSession(listener: () => void): () => void {
  listeners.add(listener);
  if (!started) {
    started = true;
    armReturnReprobe();
    booting = bootSession();
  }
  return () => {
    listeners.delete(listener);
  };
}

/** What an auth operation did. `message` is already human — render it verbatim. */
export type AuthOutcome = { ok: true } | { ok: false; message: string };

async function submit(path: string, body: Record<string, string>): Promise<AuthOutcome> {
  let res: Response;
  try {
    res = await postAuth(path, body);
  } catch {
    return { ok: false, message: GENERIC_ERROR_MESSAGE };
  }
  if (!res.ok) return { ok: false, message: await errorMessage(res) };
  await refreshSession();
  return { ok: true };
}

/** Sign in with email + password. Re-probes the session before it resolves, so a caller that
 *  navigates on `ok` navigates with the session already published. */
export async function signInWithEmail(email: string, password: string): Promise<AuthOutcome> {
  return submit(`${AUTH_NAMESPACE}/sign-in/email`, { email, password });
}

/**
 * Sign up with name + email + password. `name` is REQUIRED by the route. The server refuses, with
 * a message this returns verbatim: a password under 8 characters, a password on the common-password
 * blocklist, a password equal to the email address, and outright — "That email address is
 * reserved." — any address under `@preview.local` (the preview bridge's own namespace) and any
 * `*-drive@example.test` journey actor, which are platform identities a stranger must never claim.
 */
export async function signUpWithEmail(name: string, email: string, password: string): Promise<AuthOutcome> {
  return submit(`${AUTH_NAMESPACE}/sign-up/email`, { name, email, password });
}

export const POPUP_BLOCKED_MESSAGE = 'Enable pop-ups for this site to continue signing in.';

const PROVIDER_POLL_INTERVAL_MS = 1500;
const PROVIDER_POLL_TICKS = 200;

/** True when this document is not the top-level one — which it never is in the on.auto preview. A
 *  cross-origin parent makes `window.top` throw, and a throw is itself proof of framing. */
function isFramed(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

async function awaitProviderSession(popup: Window): Promise<AuthOutcome> {
  for (let tick = 0; tick < PROVIDER_POLL_TICKS; tick += 1) {
    const info = await refreshSession();
    if (info.status === 'authed') {
      popup.close();
      return { ok: true };
    }
    await delay(PROVIDER_POLL_INTERVAL_MS);
  }
  popup.close();
  return { ok: false, message: GENERIC_ERROR_MESSAGE };
}

/**
 * Sign in through a social provider named in the brief's `## Auth` section ('google', …).
 *
 * The route answers 200 with `{ url }` and a `Location` header — a redirect `fetch` does not
 * follow and `res.ok` does not distinguish, so a page that only checks `res.ok` reports success
 * and goes nowhere. The follow-through is this function's, and it is not a `location.assign`:
 * inside the preview iframe that navigates the FRAME, replacing the app with the provider's
 * consent screen (which refuses to be framed anyway). Framed, we open a popup first — before the
 * `await`, while the click is still the user gesture a popup blocker will accept — send it to the
 * provider, and poll `get-session` until the cookie lands on this origin.
 */
export async function signInWithProvider(provider: string): Promise<AuthOutcome> {
  if (provider.length === 0 || typeof window === 'undefined') {
    return { ok: false, message: GENERIC_ERROR_MESSAGE };
  }
  const framed = isFramed();
  const popup = framed ? window.open('', '_blank') : null;
  let res: Response;
  try {
    res = await postAuth(`${AUTH_NAMESPACE}/sign-in/social`, { provider, callbackURL: '/' });
  } catch {
    popup?.close();
    return { ok: false, message: GENERIC_ERROR_MESSAGE };
  }
  if (!res.ok) {
    popup?.close();
    return { ok: false, message: await errorMessage(res) };
  }
  const payload = (await res.json().catch(() => null)) as { url?: unknown } | null;
  const url = typeof payload?.url === 'string' ? payload.url : '';
  if (url.length === 0) {
    popup?.close();
    return { ok: false, message: GENERIC_ERROR_MESSAGE };
  }
  if (popup !== null) {
    popup.location.assign(url);
    return awaitProviderSession(popup);
  }
  if (framed) return { ok: false, message: POPUP_BLOCKED_MESSAGE };
  window.location.assign(url);
  // Never settles, on purpose: this document is leaving. Resolving would let a caller flip a form
  // back to idle and flash a "signed in" screen over a page that is already navigating away.
  return new Promise<AuthOutcome>(() => undefined);
}

/** Sign out and publish the anonymous session. Send the person somewhere public afterwards. */
export async function signOut(): Promise<AuthOutcome> {
  let res: Response;
  try {
    res = await postAuth(`${AUTH_NAMESPACE}/sign-out`);
  } catch {
    return { ok: false, message: GENERIC_ERROR_MESSAGE };
  }
  if (!res.ok) return { ok: false, message: await errorMessage(res) };
  publish({ status: 'anonymous' });
  return { ok: true };
}
